import { Injectable, signal, computed, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { BaseService } from '../../abstract/base.service';
import { PathSegments } from '../../abstract/path-segments';
import { orgPrefix } from '../utils/org-path-utils';
import { Drone } from '../data-models/drone.model';
import { CreateOrganizationDroneDto } from '../request-models/create-organization-drone-dto.model';
import { UpdateOrganizationDroneDto } from '../request-models/update-organization-drone-dto.model';
import { MaintenanceRecord } from '../data-models/maintenance-record.model';
import { CreateMaintenanceRecordDto } from '../request-models/create-maintenance-record-dto.model';
import { UpdateMaintenanceRecordDto } from '../request-models/update-maintenance-record-dto.model';
import { DroneManufacturer } from '../data-models/drone-manufacturer.model';
import { DroneModel } from '../data-models/drone-model.model';
import { toDroneRequest } from '../utils/drone-request-utils';
import { SprayRecordService } from './spray-record.service';
import { OrganizationContextService } from '../services/organization-context.service';
import { DroneManufacturerService } from './drone-manufacturer.service';
import { DroneModelService } from './drone-model.service';

@Injectable({
  providedIn: 'root'
})
export class DroneService extends BaseService<Drone> {
  private sprayRecordService = inject(SprayRecordService);
  private organizationContext = inject(OrganizationContextService);
  private droneManufacturerService = inject(DroneManufacturerService);
  private droneModelService = inject(DroneModelService);

  // Signal-based state management
  private dronesSignal = signal<Drone[]>([]);
  private manufacturersSignal = signal<DroneManufacturer[]>([]);
  private modelsSignal = signal<DroneModel[]>([]);
  private maintenanceRecordsSignal = signal<MaintenanceRecord[]>([]);
  private loadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);

  // Public computed signals for components
  readonly drones = computed(() => this.dronesSignal());
  readonly manufacturers = computed(() => this.manufacturersSignal());
  readonly models = computed(() => this.modelsSignal());
  readonly maintenanceRecords = computed(() => this.maintenanceRecordsSignal());
  readonly isLoading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());

  // Filtered drones by ownership type
  readonly adnDrones = computed(() => this.dronesSignal().filter(d => d.ownership === 'adn'));
  readonly orgDrones = computed(() => this.dronesSignal().filter(d => d.ownership === 'organization'));

  // Stats for dashboard - the API returns per-drone totals on each drone
  readonly totalDrones = computed(() => this.dronesSignal().length);
  readonly activeDrones = computed(() => this.dronesSignal().filter(d => d.status === 'active').length);

  readonly totalFlightHours = computed(() =>
    this.dronesSignal().reduce((sum, d) => sum + (d.totalFlightHours ?? 0), 0)
  );

  readonly totalAcresSprayed = computed(() =>
    this.dronesSignal().reduce((sum, d) => sum + (d.totalAcresSprayed ?? 0), 0)
  );

  constructor() {
    super('drones', Drone);
    this.loadMockManufacturers();
    this.loadMockModels();
    this.loadMockDrones();
    this.loadMockMaintenanceRecords();
  }

  private loadMockManufacturers(): void {
    const mockManufacturers: DroneManufacturer[] = [
      new DroneManufacturer({
        id: 'mfr-1',
        name: 'DJI',
        logoUrl: 'https://www.dji.com/favicon.ico'
      }),
      new DroneManufacturer({
        id: 'mfr-2',
        name: 'Hylio',
        logoUrl: undefined
      }),
      new DroneManufacturer({
        id: 'mfr-3',
        name: 'XAG',
        logoUrl: undefined
      }),
      new DroneManufacturer({
        id: 'mfr-4',
        name: 'Agras',
        logoUrl: undefined
      })
    ];

    this.manufacturersSignal.set(mockManufacturers);
  }

  private loadMockModels(): void {
    const mockModels: DroneModel[] = [
      // DJI models
      new DroneModel({
        id: 'model-1',
        manufacturerId: 'mfr-1',
        modelName: 'Agras T40',
        maxTakeoffWeightLbs: 101,
        imageUrl: undefined
      }),
      new DroneModel({
        id: 'model-2',
        manufacturerId: 'mfr-1',
        modelName: 'Agras T30',
        maxTakeoffWeightLbs: 76,
        imageUrl: undefined
      }),
      new DroneModel({
        id: 'model-3',
        manufacturerId: 'mfr-1',
        modelName: 'Agras T20P',
        maxTakeoffWeightLbs: 55,
        imageUrl: undefined
      }),
      // Hylio models
      new DroneModel({
        id: 'model-4',
        manufacturerId: 'mfr-2',
        modelName: 'AG-272',
        maxTakeoffWeightLbs: 72,
        imageUrl: undefined
      }),
      new DroneModel({
        id: 'model-5',
        manufacturerId: 'mfr-2',
        modelName: 'AG-122',
        maxTakeoffWeightLbs: 45,
        imageUrl: undefined
      }),
      // XAG models
      new DroneModel({
        id: 'model-6',
        manufacturerId: 'mfr-3',
        modelName: 'P100 Pro',
        maxTakeoffWeightLbs: 110,
        imageUrl: undefined
      }),
      new DroneModel({
        id: 'model-7',
        manufacturerId: 'mfr-3',
        modelName: 'V40',
        maxTakeoffWeightLbs: 65,
        imageUrl: undefined
      })
    ];

    this.modelsSignal.set(mockModels);
  }

  private loadMockDrones(): void {
    // Note: droneId values are used in spray records for flight history linking
    // DJI-AGRAS-001, DJI-AGRAS-002, DJI-AGRAS-003 match spray record mock data
    const mockDrones: Drone[] = [
      // ADN Leased Drones
      new Drone({
        id: 'DJI-AGRAS-001',  // Matches spray record droneId
        name: 'ADN Agras T40 #001',
        manufacturerId: 'mfr-1',
        modelId: 'model-1',
        manufacturerName: 'DJI',
        modelName: 'Agras T40',
        serialNumber: 'DJI-AT40-2024-001',
        nNumber: 'N12345',
        remoteId: 'RID-ADN-001',
        firmwareVersion: 'v4.2.1',
        ownership: 'adn',
        status: 'active',
        assignedAt: '2024-06-15T00:00:00Z',
        assignmentId: 'assign-1',
        totalFlights: 0,  // Will be computed from spray records
        totalAcresSprayed: 0,
        totalFlightHours: 0,
        lastFlightDate: undefined,
        createdAt: '2024-01-10T00:00:00Z',
        updatedAt: '2024-12-15T00:00:00Z'
      }),
      new Drone({
        id: 'DJI-AGRAS-002',  // Matches spray record droneId
        name: 'ADN Agras T30 #002',
        manufacturerId: 'mfr-1',
        modelId: 'model-2',
        manufacturerName: 'DJI',
        modelName: 'Agras T30',
        serialNumber: 'DJI-AT30-2024-002',
        nNumber: 'N12346',
        remoteId: 'RID-ADN-002',
        firmwareVersion: 'v3.8.0',
        ownership: 'adn',
        status: 'active',
        assignedAt: '2024-08-01T00:00:00Z',
        assignmentId: 'assign-2',
        totalFlights: 0,
        totalAcresSprayed: 0,
        totalFlightHours: 0,
        lastFlightDate: undefined,
        createdAt: '2024-02-15T00:00:00Z',
        updatedAt: '2024-12-12T00:00:00Z'
      }),
      new Drone({
        id: 'DJI-AGRAS-003',  // Matches spray record droneId
        name: 'ADN Agras T40 #003',
        manufacturerId: 'mfr-1',
        modelId: 'model-1',
        manufacturerName: 'DJI',
        modelName: 'Agras T40',
        serialNumber: 'DJI-AT40-2024-003',
        nNumber: 'N12347',
        remoteId: 'RID-ADN-003',
        firmwareVersion: 'v4.2.1',
        ownership: 'adn',
        status: 'active',
        assignedAt: '2024-09-10T00:00:00Z',
        assignmentId: 'assign-3',
        totalFlights: 0,
        totalAcresSprayed: 0,
        totalFlightHours: 0,
        lastFlightDate: undefined,
        createdAt: '2024-03-20T00:00:00Z',
        updatedAt: '2024-12-01T00:00:00Z'
      }),
      new Drone({
        id: 'adn-4',
        name: 'ADN Hylio AG-272 #004',
        manufacturerId: 'mfr-2',
        modelId: 'model-4',
        manufacturerName: 'Hylio',
        modelName: 'AG-272',
        serialNumber: 'HYL-AG272-2024-001',
        nNumber: 'N12348',
        remoteId: 'RID-ADN-004',
        firmwareVersion: 'v2.5.0',
        ownership: 'adn',
        status: 'maintenance',
        assignedAt: '2024-10-01T00:00:00Z',
        assignmentId: 'assign-4',
        totalFlights: 0,
        totalAcresSprayed: 0,
        totalFlightHours: 0,
        lastFlightDate: undefined,
        createdAt: '2024-04-15T00:00:00Z',
        updatedAt: '2024-12-01T00:00:00Z'
      }),
      // Organization-Owned Drones
      new Drone({
        id: 'org-1',
        name: 'Farm Sprayer #1',
        manufacturerId: 'mfr-1',
        modelId: 'model-2',
        manufacturerName: 'DJI',
        modelName: 'Agras T30',
        serialNumber: 'CUSTOM-T30-001',
        nNumber: 'N54321',
        remoteId: 'RID-ORG-001',
        firmwareVersion: 'v3.8.0',
        ownership: 'organization',
        status: 'active',
        totalFlights: 0,
        totalAcresSprayed: 0,
        totalFlightHours: 0,
        lastFlightDate: undefined,
        createdAt: '2024-05-01T00:00:00Z',
        updatedAt: '2024-12-10T00:00:00Z'
      }),
      new Drone({
        id: 'org-2',
        name: 'Orchard Drone',
        manufacturerId: 'mfr-3',
        modelId: 'model-7',
        manufacturerName: 'XAG',
        modelName: 'V40',
        serialNumber: 'XAG-V40-ORG-001',
        firmwareVersion: 'v1.9.2',
        ownership: 'organization',
        status: 'active',
        totalFlights: 0,
        totalAcresSprayed: 0,
        totalFlightHours: 0,
        lastFlightDate: undefined,
        createdAt: '2024-07-15T00:00:00Z',
        updatedAt: '2024-12-08T00:00:00Z'
      }),
      new Drone({
        id: 'org-3',
        name: 'Backup Unit',
        manufacturerId: 'mfr-2',
        modelId: 'model-5',
        manufacturerName: 'Hylio',
        modelName: 'AG-122',
        serialNumber: 'HYL-AG122-ORG-001',
        ownership: 'organization',
        status: 'inactive',
        totalFlights: 0,
        totalAcresSprayed: 0,
        totalFlightHours: 0,
        lastFlightDate: undefined,
        createdAt: '2024-06-01T00:00:00Z',
        updatedAt: '2024-09-15T00:00:00Z'
      })
    ];

    this.dronesSignal.set(mockDrones);
  }

  private loadMockMaintenanceRecords(): void {
    const mockRecords: MaintenanceRecord[] = [
      {
        id: 'maint-1',
        droneId: 'DJI-AGRAS-001',
        type: 'scheduled',
        title: 'Annual Inspection',
        description: 'Comprehensive annual inspection and calibration',
        scheduledDate: '2024-12-20',
        status: 'scheduled',
        performedBy: 'ADN Service Center',
        createdAt: '2024-11-15T00:00:00Z',
        updatedAt: '2024-11-15T00:00:00Z'
      },
      {
        id: 'maint-2',
        droneId: 'adn-4',
        type: 'repair',
        title: 'Motor Replacement',
        description: 'Replace front-left motor due to bearing wear',
        scheduledDate: '2024-12-01',
        completedDate: '2024-12-05',
        status: 'in_progress',
        performedBy: 'Hylio Authorized Service',
        cost: 450,
        createdAt: '2024-11-28T00:00:00Z',
        updatedAt: '2024-12-05T00:00:00Z'
      },
      {
        id: 'maint-3',
        droneId: 'org-1',
        type: 'inspection',
        title: '100-Hour Inspection',
        description: 'Regular 100 flight hour inspection',
        scheduledDate: '2024-11-15',
        completedDate: '2024-11-15',
        status: 'completed',
        performedBy: 'In-house',
        cost: 0,
        notes: 'All systems nominal. Props replaced preventively.',
        createdAt: '2024-11-10T00:00:00Z',
        updatedAt: '2024-11-15T00:00:00Z'
      }
    ];

    this.maintenanceRecordsSignal.set(mockRecords);
  }

  // ==================== API Methods ====================

  /**
   * Load manufacturers and models from the API.
   * Failures keep the current lookups (the mock lists until the first successful load).
   */
  async loadLookups(): Promise<void> {
    try {
      const manufacturers = await firstValueFrom(this.droneManufacturerService.getMany());
      this.manufacturersSignal.set(manufacturers);

      const models = await firstValueFrom(this.droneModelService.getMany());
      this.modelsSignal.set(models);
    } catch (error: unknown) {
      console.error('[DroneService] Error loading lookups:', ApiError.fromHttpError(error).message);
    }
  }

  /**
   * Load drones assigned to the current organization from the API
   * Includes both ADN-assigned drones and organization-owned drones
   */
  async loadOrganizationDrones(): Promise<void> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) {
      console.warn('[DroneService] No organization selected');
      return;
    }

    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const drones = await firstValueFrom(this.getMany({ prefix: orgPrefix(orgId) }));
      this.dronesSignal.set(drones);
    } catch (error: unknown) {
      const message = ApiError.fromHttpError(error).errors[0] ?? 'Failed to load drones';
      console.error('[DroneService] Error loading organization drones:', message);
      this.errorSignal.set(message);
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * The current organization's drones (ADN-assigned and organization-owned), straight from the API
   * without touching the cached list. Rejects with an `ApiError`. Use it for drone pickers.
   */
  async getOrganizationDrones(): Promise<Drone[]> {
    return firstValueFrom(this.getMany({ prefix: this.currentOrganizationPrefix() }));
  }

  // ==================== Drone CRUD Operations ====================

  getDrones(): Drone[] {
    return this.drones();
  }

  getDroneById(id: string): Drone | undefined {
    return this.dronesSignal().find(d => d.id === id);
  }

  /**
   * Fetch a single drone from the API by ID.
   * Used when drone may not be in local cache (e.g., navigating from activity link).
   * The Hub scopes `GET /drones/:id` to the organization the interceptor sends.
   */
  async fetchDroneById(id: string): Promise<Drone | null> {
    try {
      const drone = await firstValueFrom(this.get(id));
      if (drone && !this.dronesSignal().find(d => d.id === id)) {
        this.dronesSignal.update(drones => [...drones, drone]);
      }
      return drone;
    } catch (error: unknown) {
      console.error('[DroneService] Error fetching drone by ID:', ApiError.fromHttpError(error).message);
      return null;
    }
  }

  /**
   * Get models filtered by manufacturer ID (for cascading dropdown)
   */
  getModelsByManufacturer(manufacturerId: string): DroneModel[] {
    return this.modelsSignal().filter(m => m.manufacturerId === manufacturerId);
  }

  /**
   * Get manufacturer name by ID
   */
  getManufacturerName(manufacturerId: string): string {
    const manufacturer = this.manufacturersSignal().find(m => m.id === manufacturerId);
    return manufacturer?.name || 'Unknown';
  }

  /**
   * Get model name by ID
   */
  getModelName(modelId: string): string {
    const model = this.modelsSignal().find(m => m.id === modelId);
    return model?.modelName || 'Unknown';
  }

  /**
   * Create a new organization-owned drone.
   * ADN drones cannot be created through the web portal.
   * Throws an `ApiError` (message = the Hub's error) when the Hub rejects it.
   */
  async createDrone(dto: CreateOrganizationDroneDto): Promise<Drone> {
    const request = toDroneRequest({ ...dto, status: dto.status || 'active' });
    const created = await firstValueFrom(this.create(request, { prefix: this.currentOrganizationPrefix() }));
    if (!created) {
      throw new Error('Failed to create drone');
    }

    // Only organization drones can be created; the create response has no ownership or stats.
    created.ownership = 'organization';
    created.createdAt ??= new Date();
    created.updatedAt ??= created.createdAt;
    this.dronesSignal.update(drones => [...drones, created]);
    return created;
  }

  /**
   * Update an organization-owned drone.
   * ADN drones cannot be edited through the web portal.
   * Throws an `ApiError` (message = the Hub's error) when the Hub rejects it.
   */
  async updateDrone(id: string, dto: UpdateOrganizationDroneDto): Promise<Drone | null> {
    const drone = this.getDroneById(id);

    // Cannot edit ADN drones
    if (!drone || drone.ownership === 'adn') {
      return null;
    }

    const saved = await firstValueFrom(this.update(id, toDroneRequest(dto), { prefix: this.currentOrganizationPrefix() }));
    if (!saved) {
      throw new Error('Failed to update drone');
    }

    const updatedDrone = this.keepCachedDisplayFields(saved, drone);
    this.dronesSignal.update(drones => drones.map(d => (d.id === id ? updatedDrone : d)));
    return updatedDrone;
  }

  /**
   * Delete an organization-owned drone.
   * ADN drones cannot be deleted through the web portal.
   * Throws an `ApiError` (message = the Hub's error) when the Hub rejects it.
   */
  async deleteDrone(id: string): Promise<boolean> {
    const drone = this.getDroneById(id);

    // Cannot delete ADN drones
    if (!drone || drone.ownership === 'adn') {
      return false;
    }

    await firstValueFrom(this.delete(id, { prefix: this.currentOrganizationPrefix() }));

    // Update local state, including the drone's (mock) maintenance records
    this.dronesSignal.update(drones => drones.filter(d => d.id !== id));
    this.maintenanceRecordsSignal.update(records =>
      records.filter(r => r.droneId !== id)
    );
    return true;
  }

  /**
   * Check if a drone can be edited (organization-owned only)
   */
  canEdit(drone: Drone): boolean {
    return drone.ownership === 'organization';
  }

  /**
   * Check if a drone can be deleted (organization-owned only)
   */
  canDelete(drone: Drone): boolean {
    return drone.ownership === 'organization';
  }

  // ==================== Maintenance Record Operations ====================

  getMaintenanceByDroneId(droneId: string): MaintenanceRecord[] {
    return this.maintenanceRecordsSignal().filter(r => r.droneId === droneId);
  }

  getUpcomingMaintenance(droneId: string): MaintenanceRecord | undefined {
    const records = this.getMaintenanceByDroneId(droneId);
    const upcoming = records
      .filter(r => r.status === 'scheduled' || r.status === 'in_progress')
      .sort((a, b) => new Date(a.scheduledDate).getTime() - new Date(b.scheduledDate).getTime());
    return upcoming[0];
  }

  createMaintenanceRecord(dto: CreateMaintenanceRecordDto): MaintenanceRecord {
    const newRecord: MaintenanceRecord = {
      id: 'maint-' + Date.now(),
      droneId: dto.droneId,
      type: dto.type,
      title: dto.title,
      description: dto.description,
      scheduledDate: dto.scheduledDate,
      status: 'scheduled',
      performedBy: dto.performedBy,
      cost: dto.cost,
      notes: dto.notes,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    this.maintenanceRecordsSignal.update(records => [...records, newRecord]);
    return newRecord;
  }

  updateMaintenanceRecord(id: string, dto: UpdateMaintenanceRecordDto): MaintenanceRecord | null {
    const index = this.maintenanceRecordsSignal().findIndex(r => r.id === id);
    if (index === -1) {
      return null;
    }

    const updatedRecord: MaintenanceRecord = {
      ...this.maintenanceRecordsSignal()[index],
      ...dto,
      updatedAt: new Date().toISOString()
    };

    this.maintenanceRecordsSignal.update(records => {
      const newRecords = [...records];
      newRecords[index] = updatedRecord;
      return newRecords;
    });

    return updatedRecord;
  }

  deleteMaintenanceRecord(id: string): boolean {
    const index = this.maintenanceRecordsSignal().findIndex(r => r.id === id);
    if (index === -1) {
      return false;
    }

    this.maintenanceRecordsSignal.update(records => records.filter(r => r.id !== id));
    return true;
  }

  // ==================== Stats for Dashboard ====================

  getDroneStats(): { total: number; active: number; flightHours: number; acresSprayed: number } {
    return {
      total: this.totalDrones(),
      active: this.activeDrones(),
      flightHours: this.totalFlightHours(),
      acresSprayed: this.totalAcresSprayed()
    };
  }

  // ==================== Drone Stats ====================

  /**
   * Get flight stats for a drone.
   * The API aggregates these per drone, so nothing is computed here.
   */
  getDroneFlightStats(droneId: string): {
    totalFlights: number;
    totalAcresSprayed: number;
    totalFlightHours: number;
    lastFlightDate: string | undefined;
  } {
    const drone = this.getDroneById(droneId);

    return {
      totalFlights: drone?.totalFlights ?? 0,
      totalAcresSprayed: drone?.totalAcresSprayed ?? 0,
      totalFlightHours: drone?.totalFlightHours ?? 0,
      lastFlightDate: drone?.lastFlightDate ?? undefined
    };
  }

  /**
   * Get count of flights for a specific drone
   */
  getFlightCountForDrone(droneId: string): number {
    return this.getDroneFlightStats(droneId).totalFlights;
  }

  // ==================== BaseService ====================

  /** `organizations/{id}` for the organization the user is working under. */
  private currentOrganizationPrefix(): PathSegments {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) {
      throw new Error('No organization selected');
    }
    return orgPrefix(orgId);
  }

  /**
   * The update response carries the saved editable fields but not ownership, assignment or flight stats,
   * so those are kept from the cached drone. `saved` is a fresh instance, so it is safe to fill in.
   */
  private keepCachedDisplayFields(saved: Drone, cached: Drone): Drone {
    saved.ownership = cached.ownership;
    saved.assignedAt = cached.assignedAt;
    saved.assignmentId = cached.assignmentId;
    saved.totalFlights = cached.totalFlights;
    saved.totalAcresSprayed = cached.totalAcresSprayed;
    saved.totalFlightHours = cached.totalFlightHours;
    saved.lastFlightDate = cached.lastFlightDate;
    saved.createdAt ??= cached.createdAt;
    saved.updatedAt ??= new Date();
    return saved;
  }
}
